import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { type OrderRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { OrderDialog } from '@/components/OrderDialog';
import { OrderDeleteDialog } from '@/components/OrderDeleteDialog';
import { OrderStatusPill } from '@/components/OrderStatusPill';
import { formatEuro } from '@/lib/utils';

interface Props {
  orders: OrderRow[];
  onChanged: () => void;
}

/**
 * Aufträge der Firma.
 *
 * Eine Zeile öffnet den Auftrag zum Bearbeiten — dort stehen auch die
 * Positionen und der Statuswechsel —, der Papierkorb löscht ihn nach
 * Rückfrage. Neu angelegt wird nicht hier, sondern über „Neu“ auf Heute oder
 * am Projekt. Ohne Aufträge bleibt der Reiter leer. Eine eigene Auftragsseite
 * gibt es nicht mehr; bis Aufträge in einem Projektreiter wohnen, ist dies
 * einer von zwei Orten, an denen man sie ändern kann.
 */
export function OrdersTab({ orders, onChanged }: Props) {
  const [bearbeiten, setBearbeiten] = useState<OrderRow | null>(null);
  const [loeschen, setLoeschen] = useState<OrderRow | null>(null);

  if (orders.length === 0) return null;

  return (
    <Card className="p-6">
      <h2 className="text-sm font-semibold">Aufträge</h2>
      <ul className="mt-3 divide-y">
        {orders.map((a) => (
          <li key={a.id} className="flex items-center justify-between gap-3 py-2.5">
            <button
              type="button"
              className="min-w-0 flex-1 text-left hover:underline"
              onClick={() => setBearbeiten(a)}
            >
              <span className="block text-sm font-medium">
                <span className="font-mono text-xs text-muted-foreground">
                  {a.number}
                </span>{' '}
                {a.title}
              </span>
              {a.totalTasks > 0 && (
                <span className="block text-xs text-muted-foreground">
                  Aufgaben {a.totalTasks - a.openTasks}/{a.totalTasks}
                </span>
              )}
            </button>
            <div className="flex shrink-0 items-center gap-3">
              {a.valueCents > 0 && (
                <span className="text-sm">{formatEuro(a.valueCents)}</span>
              )}
              <OrderStatusPill status={a.status as never} />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`${a.number} löschen`}
                onClick={() => setLoeschen(a)}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {bearbeiten && (
        <OrderDialog
          order={bearbeiten}
          onClose={() => setBearbeiten(null)}
          onSaved={() => {
            setBearbeiten(null);
            onChanged();
          }}
        />
      )}
      {loeschen && (
        <OrderDeleteDialog
          order={loeschen}
          onCancel={() => setLoeschen(null)}
          onDeleted={() => {
            setLoeschen(null);
            onChanged();
          }}
        />
      )}
    </Card>
  );
}
